import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Shield, Smartphone, KeyRound, LogOut, RefreshCcw, Download } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import {
  multiFactor,
  TotpMultiFactorGenerator,
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendEmailVerification,
} from 'firebase/auth';
import { FIREBASE_CONFIG } from '../../config/Config';
import { saveBackupCodes } from '../../services/MfaSetupRepository';

// Art. 90.2 — Emissor oficial imútável
const MFA_APP_NAME = 'Rodrigo Demeterco Advocacia';
const APP_ID = FIREBASE_CONFIG.projectId || 'portalrda-f312d';

// Art. 90.1 — hashing seguro via Web Crypto API
const hashBackupCode = async (code) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(code);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hashBuffer)).map((b) => b.toString(16).padStart(2, '0')).join('');
};

// Art. 90.1 — gera 10 códigos em memória (plain + hashed)
const generateBackupCodesInMemory = async () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const codes = [];
  const hashed = [];
  for (let i = 0; i < 10; i++) {
    let raw = '';
    const rand = new Uint32Array(8);
    window.crypto.getRandomValues(rand);
    for (let j = 0; j < 8; j++) raw += chars[rand[j] % chars.length];
    const formatted = `${raw.slice(0, 4)}-${raw.slice(4)}`;
    codes.push(formatted);
    hashed.push({ hash: await hashBackupCode(formatted), used: false });
  }
  return { plain: codes, hashed };
};

const readEnrolledFactorCount = (user) => {
  if (!user) return 0;
  const direct = Array.isArray(user?.multiFactor?.enrolledFactors) ? user.multiFactor.enrolledFactors.length : null;
  if (typeof direct === 'number') return direct;
  try {
    const factors = multiFactor(user)?.enrolledFactors;
    return Array.isArray(factors) ? factors.length : 0;
  } catch {
    return 0;
  }
};

export default function MfaSetup({ user, onComplete, onLogout }) {
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState('intro');
  const [secret, setSecret] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [copied, setCopied] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [backupCodes, setBackupCodes] = useState([]);
  const [hasDownloaded, setHasDownloaded] = useState(false);

  const enrolledFactorCount = useMemo(() => readEnrolledFactorCount(user), [user, refreshTick]);
  const hasEnrolledFactor = enrolledFactorCount > 0;

  useEffect(() => {
    if (hasEnrolledFactor && typeof onComplete === 'function') {
      onComplete();
    }
  }, [hasEnrolledFactor, onComplete]);

  const handleSendVerificationEmail = useCallback(async () => {
    if (!user) return;
    setError('');
    setInfo('');
    try {
      await sendEmailVerification(user);
      setInfo(`E-mail de verificação reenviado para ${user.email}. Verifique também a caixa de spam.`);
    } catch (e) {
      setError(`Não foi possível reenviar o e-mail: ${e?.message || e}`);
    }
  }, [user]);

  const handleGenerateSecret = useCallback(async () => {
    if (!user?.email) {
      setError('Usuário autenticado não encontrado para configurar o MFA.');
      return;
    }

    if (!user.emailVerified) {
      setError('Seu e-mail ainda não foi verificado. Verifique o e-mail antes de ativar o MFA.');
      return;
    }

    if (!String(password || '').trim()) {
      setError('Confirme sua senha atual para iniciar a inscrição do autenticador.');
      return;
    }

    setStep('loading');
    setError('');
    setInfo('');

    try {
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);

      const multiFactorSession = await multiFactor(user).getSession();
      const totpSecret = await TotpMultiFactorGenerator.generateSecret(multiFactorSession);
      const safeEmail = String(user.email || '').trim() || 'usuario@rda.adv.br';
      const nextQrCodeUrl = totpSecret.generateQrCodeUrl(safeEmail, MFA_APP_NAME);

      setSecret(totpSecret);
      setSecretKey(String(totpSecret?.secretKey || '').trim());
      setQrCodeUrl(nextQrCodeUrl);
      setStep('setup');
    } catch (e) {
      console.error('[RDA MFA] erro ao gerar segredo:', e);
      if (e?.code === 'auth/multi-factor-auth-required') {
        try {
          await user.reload();
        } catch (reloadError) {
          void reloadError;
        }

        setRefreshTick((v) => v + 1);
        const refreshedFactorCount = readEnrolledFactorCount(user);
        if (refreshedFactorCount > 0) {
          setInfo('Seu usuário já possui um fator MFA ativo. Liberando o acesso administrativo.');
          setStep('success');
          if (typeof onComplete === 'function') onComplete();
          return;
        }
      }

      setStep('intro');
      if (e?.code === 'auth/wrong-password' || e?.code === 'auth/invalid-credential') {
        setError('Senha inválida. Confirme sua senha atual e tente novamente.');
      } else if (e?.code === 'auth/multi-factor-auth-required') {
        setError('Sua sessão exige MFA para reautenticação. Faça novo login completo e tente novamente se a inscrição ainda estiver pendente.');
      } else if (e?.code === 'auth/unverified-email') {
        setError('Seu e-mail precisa estar verificado para ativar o MFA.');
      } else {
        setError(`Falha ao iniciar o setup do MFA: ${e?.message || e}`);
      }
    }
  }, [password, user]);

  const handleEnroll = useCallback(async () => {
    if (!user) {
      setError('Usuário não disponível.');
      return;
    }
    if (!secret) {
      setError('O segredo TOTP ainda não foi gerado.');
      return;
    }

    const sanitizedCode = String(verificationCode || '').replace(/\D/g, '').slice(0, 6);
    if (sanitizedCode.length !== 6) {
      setError('Digite os 6 dígitos gerados no Google Authenticator.');
      return;
    }

    setStep('verifying');
    setError('');
    setInfo('');

    try {
      const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, sanitizedCode);
      await multiFactor(user).enroll(multiFactorAssertion, 'Google Authenticator');
      await user.reload();
      setRefreshTick((v) => v + 1);

      // Art. 90.1: gerar códigos em memória ANTES de qualquer UI update
      const generated = await generateBackupCodesInMemory();

      // Art. 90.1: persistir no Firestore ANTES de exibir na tela
      try {
        await saveBackupCodes(user.uid, generated.hashed);
      } catch (saveErr) {
        console.error('[RDA MFA] Erro ao salvar backup codes no Firestore:', saveErr);
        alert('⚠️ ALERTA: MFA ativado, mas não foi possível salvar os códigos no servidor. Anote os códigos da próxima tela manualmente.');
      }

      setBackupCodes(generated.plain);
      setStep('backup');
    } catch (e) {
      console.error('[RDA MFA] erro ao ativar MFA:', e);
      setStep('setup');
      if (e?.code === 'auth/invalid-verification-code') {
        setError('Código inválido, tente novamente com os 6 dígitos atuais do aplicativo.');
      } else if (e?.code === 'auth/code-expired') {
        setError('Código expirado. Aguarde o próximo código do Authenticator e tente de novo.');
      } else {
        setError(`Não foi possível ativar o MFA: ${e?.message || e}`);
      }
    }
  }, [secret, user, verificationCode]);

  const downloadBackupCodes = useCallback(() => {
    const link = document.createElement('a');
    const blob = new Blob([
      `CÓDIGOS DE RECUPERAÇÃO — RODRIGO DEMETERCO ADVOCACIA\n\nGuarde em local OFFLINE e SEGURO.\nPerde o celular? Estes códigos são sua Única forma de entrar.\n\n${backupCodes.join('\n')}`
    ], { type: 'text/plain' });
    link.href = URL.createObjectURL(blob);
    link.download = 'RDA_Codigos_Recuperacao.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setHasDownloaded(true);
  }, [backupCodes]);

  const handleCopySecret = useCallback(async () => {
    if (!secretKey) return;
    try {
      await navigator.clipboard.writeText(secretKey);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      setError(`Não foi possível copiar a chave secreta: ${e?.message || e}`);
    }
  }, [secretKey]);

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#243B6B_0%,_#0F172A_45%,_#020617_100%)] px-4 py-8 text-white">
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-6xl items-center justify-center">
        <div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <section className="rounded-3xl border border-white/10 bg-white/8 p-6 shadow-2xl backdrop-blur-xl md:p-8">
            <div className="flex items-start gap-4">
              <div className="rounded-2xl bg-emerald-500/15 p-4 text-emerald-300">
                <Shield className="h-8 w-8" />
              </div>
              <div>
                <div className="text-[11px] font-extrabold uppercase tracking-[0.25em] text-emerald-300">Military Grade Security</div>
                <h1 className="mt-2 text-2xl font-black text-white md:text-3xl">Ative o MFA por Google Authenticator</h1>
                <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-200">
                  Para acessar o ERP administrativo, sua conta precisa concluir a inscrição TOTP.
                  Baixe o Google Authenticator, escaneie o QR Code e valide o código de 6 dígitos.
                </p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="rounded-2xl border border-white/10 bg-slate-950/30 p-4">
                <div className="flex items-center gap-2 text-emerald-300"><Smartphone className="h-4 w-4" /><span className="text-[11px] font-extrabold uppercase tracking-widest">Passo 1</span></div>
                <div className="mt-2 text-sm font-bold text-white">Confirme sua senha</div>
                <div className="mt-1 text-xs leading-5 text-slate-300">Reautentique sua sessão para gerar o segredo TOTP do Firebase.</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-slate-950/30 p-4">
                <div className="flex items-center gap-2 text-cyan-300"><Shield className="h-4 w-4" /><span className="text-[11px] font-extrabold uppercase tracking-widest">Passo 2</span></div>
                <div className="mt-2 text-sm font-bold text-white">Escaneie o QR Code</div>
                <div className="mt-1 text-xs leading-5 text-slate-300">Use Google Authenticator ou app TOTP compatível para registrar seu dispositivo.</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-slate-950/30 p-4">
                <div className="flex items-center gap-2 text-amber-300"><KeyRound className="h-4 w-4" /><span className="text-[11px] font-extrabold uppercase tracking-widest">Passo 3</span></div>
                <div className="mt-2 text-sm font-bold text-white">Digite o código</div>
                <div className="mt-1 text-xs leading-5 text-slate-300">Valide os 6 dígitos para blindar definitivamente sua conta administrativa.</div>
              </div>
            </div>

            {error ? (
              <div className="mt-6 rounded-2xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-100">{error}</div>
            ) : null}

            {info ? (
              <div className="mt-6 rounded-2xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">{info}</div>
            ) : null}

            {!user?.emailVerified ? (
              <div className="mt-6 rounded-2xl border border-amber-300/30 bg-amber-500/10 p-4">
                <div className="text-xs font-extrabold uppercase tracking-widest text-amber-200">E-mail pendente</div>
                <div className="mt-2 text-sm text-amber-50">Seu e-mail precisa estar verificado antes de ativar o MFA TOTP.</div>
                <button
                  type="button"
                  onClick={handleSendVerificationEmail}
                  className="mt-4 inline-flex items-center gap-2 rounded-xl border border-amber-300/40 bg-white/10 px-4 py-3 text-xs font-extrabold uppercase tracking-widest text-amber-50 hover:bg-white/15"
                >
                  <RefreshCcw className="h-4 w-4" /> Reenviar e-mail de verificação
                </button>
              </div>
            ) : null}

            <div className="mt-6 rounded-3xl border border-white/10 bg-slate-950/30 p-5 md:p-6">
              <div className="text-[11px] font-extrabold uppercase tracking-[0.24em] text-slate-300">Confirmação de identidade</div>
              <div className="mt-2 text-sm text-slate-200">Sessão atual: <span className="font-bold text-white">{user?.email || 'sem e-mail'}</span></div>
              <div className="mt-4">
                <label className="mb-2 block text-[11px] font-extrabold uppercase tracking-widest text-slate-300">Senha atual</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Digite sua senha para gerar o QR Code"
                  className="w-full rounded-2xl border border-white/10 bg-white/10 px-4 py-4 text-sm text-white outline-none placeholder:text-slate-400 focus:border-emerald-300/60 focus:ring-2 focus:ring-emerald-300/20"
                />
              </div>

              <button
                type="button"
                onClick={handleGenerateSecret}
                disabled={step === 'loading' || step === 'verifying' || !user?.emailVerified}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-500 px-4 py-4 text-xs font-extrabold uppercase tracking-[0.22em] text-slate-950 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {step === 'loading' ? 'Gerando segredo TOTP...' : 'Gerar QR Code do Google Authenticator'}
              </button>
            </div>
          </section>

          <aside className="rounded-3xl border border-white/10 bg-slate-950/60 p-6 shadow-2xl backdrop-blur-xl md:p-8">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-[11px] font-extrabold uppercase tracking-[0.24em] text-cyan-300">Enrollment Center</div>
                <div className="mt-2 text-lg font-black text-white">Setup do dispositivo autenticador</div>
              </div>
              {typeof onLogout === 'function' ? (
                <button
                  type="button"
                  onClick={onLogout}
                  className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-[11px] font-extrabold uppercase tracking-widest text-slate-200 hover:bg-white/10"
                >
                  <LogOut className="h-4 w-4" /> Sair
                </button>
              ) : null}
            </div>

            {hasEnrolledFactor ? (
              <div className="mt-6 rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-5 text-sm text-emerald-50">
                Seu usuário já possui um fator MFA cadastrado. Liberando o acesso administrativo.
              </div>
            ) : secret && qrCodeUrl ? (
              <div className="mt-6 space-y-5">
                <div className="rounded-3xl border border-white/10 bg-white p-5 text-slate-900">
                  <div className="flex justify-center">
                    <QRCodeSVG value={qrCodeUrl} size={220} includeMargin />
                  </div>
                  <div className="mt-4 text-center text-xs font-bold uppercase tracking-widest text-slate-500">Escaneie com Google Authenticator</div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="text-[11px] font-extrabold uppercase tracking-widest text-slate-300">Chave secreta manual</div>
                  <div className="mt-2 break-all rounded-xl bg-slate-950/60 px-3 py-3 font-mono text-sm text-emerald-200">{secretKey || 'aguardando chave'}</div>
                  <button
                    type="button"
                    onClick={handleCopySecret}
                    className="mt-3 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-[11px] font-extrabold uppercase tracking-widest text-slate-200 hover:bg-white/10"
                  >
                    {copied ? 'Chave copiada' : 'Copiar chave secreta'}
                  </button>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <label className="mb-2 block text-[11px] font-extrabold uppercase tracking-widest text-slate-300">Código de 6 dígitos</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(String(e.target.value || '').replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="w-full rounded-2xl border border-white/10 bg-white/10 px-4 py-4 text-center text-2xl font-black tracking-[0.35em] text-white outline-none placeholder:text-slate-500 focus:border-cyan-300/60 focus:ring-2 focus:ring-cyan-300/20"
                  />
                  <button
                    type="button"
                    onClick={handleEnroll}
                    disabled={step === 'verifying'}
                    className="mt-4 inline-flex w-full items-center justify-center rounded-2xl bg-cyan-400 px-4 py-4 text-xs font-extrabold uppercase tracking-[0.22em] text-slate-950 transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {step === 'verifying' ? 'Validando código...' : 'Ativar MFA agora'}
                  </button>
                </div>
              </div>
            ) : step === 'backup' ? (
              <div className="mt-6 space-y-5">
                <div className="rounded-2xl border border-amber-400/30 bg-amber-500/10 p-4">
                  <div className="text-[11px] font-extrabold uppercase tracking-widest text-amber-200">⚠️ Art. 90.1 — Códigos de Backup</div>
                  <div className="mt-1 text-xs text-amber-50">MFA ativado. Estes códigos foram salvos no servidor. Guarde-os também OFFLINE.</div>
                </div>
                <div className="grid grid-cols-2 gap-2 rounded-2xl bg-slate-950 p-4 font-mono text-xs text-emerald-300 border border-white/10">
                  {backupCodes.map((code, i) => (
                    <div key={i} className="rounded-lg bg-black/30 px-2 py-1 text-center tracking-widest">{code}</div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={downloadBackupCodes}
                  className={`inline-flex w-full items-center justify-center gap-2 rounded-2xl px-4 py-4 text-xs font-extrabold uppercase tracking-[0.22em] text-slate-950 transition ${hasDownloaded ? 'bg-emerald-400 hover:bg-emerald-300' : 'bg-amber-400 hover:bg-amber-300'}`}
                >
                  <Download className="h-4 w-4" />
                  {hasDownloaded ? 'Baixar Novamente' : 'Baixar Códigos (.TXT)'}
                </button>
                <button
                  type="button"
                  disabled={!hasDownloaded}
                  onClick={() => { if (typeof onComplete === 'function') onComplete(); }}
                  className="inline-flex w-full items-center justify-center rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs font-extrabold uppercase tracking-widest text-slate-200 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40 transition"
                >
                  {hasDownloaded ? 'Continuar (já salvei)' : 'Baixe para continuar'}
                </button>
              </div>
            ) : step === 'success' ? (
              <div className="mt-6 rounded-2xl border border-emerald-400/30 bg-emerald-500/10 p-5 text-sm text-emerald-50">
                Conta blindada com TOTP. Liberando o acesso administrativo...
              </div>
            ) : (
              <div className="mt-6 rounded-3xl border border-dashed border-white/10 bg-white/5 p-6 text-sm leading-6 text-slate-300">
                Quando você confirmar sua senha, o sistema vai gerar a Secret Key do Firebase TOTP e exibir aqui o QR Code do autenticador.
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
